import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { SessionRecordingSummary } from "@/types";

// While the recording is still transcoding (pending) or only partially
// playable, keep polling so the page flips to the player on its own — the
// VOD-only analogue of the public watch page's getRecordingsPollInterval.
const POLL_INTERVAL_MS = 30_000;
const POLLABLE_STATUSES = new Set<SessionRecordingSummary["playbackStatus"]>([
  "pending",
  "partial",
]);

// Exported for direct unit testing, mirroring getRecordingsPollInterval.
export function getAdminWatchPollInterval(
  recording: SessionRecordingSummary | undefined,
): number | false {
  return recording && POLLABLE_STATUSES.has(recording.playbackStatus)
    ? POLL_INTERVAL_MS
    : false;
}

/**
 * Data layer for the admin-only /admin/watch/:recordingId page: one recording
 * in the RecordingSummary shape plus its session context, from
 * GET /admin/recordings/:id/watch. Backend access control is the JWT +
 * ADMIN_EMAILS allowlist; a 403 surfaces here as an error for the page to
 * render. Deliberately performs NO view counting.
 */
export function useAdminRecordingWatch(recordingId: string) {
  const query = useQuery({
    queryKey: ["admin", "recordings", recordingId, "watch"],
    queryFn: () => apiClient.getAdminRecordingWatch(recordingId),
    enabled: !!recordingId,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) =>
      getAdminWatchPollInterval(query.state.data?.recording),
  });

  return {
    recording: query.data?.recording,
    session: query.data?.session,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
